"use client";

import { CURRENCY } from "@/data/numbers";
import { routes } from "@/data/routes";
import { GlobalConsumer } from "@/data/store";
import { type Cart } from "@/lib/cart";
import { cn } from "@/lib/components";
import { formatPrice } from "@/lib/numbers";
import Link from "next/link";
import { Button } from "../base/button";
import { CartItems } from "../blocks/CartItems";
import { HomeSection, HomeServiceCard } from "../blocks/Home";
import homeData from "../blocks/Home.data";

export function CartContainer() {
  return (
    <>
      <GlobalConsumer
        getters={["cartItems"]}
        mutations={["addToCart", "decrementProductQuantity", "removeFromCart"]}
      >
        {({ cartItems, addToCart, decrementProductQuantity, removeFromCart }) => {
          const subtotal = calculateCartSubtotal(cartItems);
          const shipping = calculateShipping(subtotal);
          const tax = calculateTax(subtotal);
          const total = subtotal + shipping + tax;

          return (
            <div className="m-auto flex max-w-[1440px] flex-col gap-8 p-2 lg:p-8">
              <h1 className="flex gap-1 text-2xl font-bold">
                Shopping Cart
                <span className="font-medium text-gray-500">{`(${cartItems.length} ${cartItems.length === 1 ? "item" : "items"})`}</span>
              </h1>

              {cartItems.length === 0 ? (
                <div role="status" aria-live="polite" className="py-12 text-center">
                  <p className="mb-4 text-lg text-gray-600">Your cart is empty</p>
                  <Button asChild variant="default" size="lg">
                    <Link href={routes.products.path}>Continue Shopping</Link>
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start">
                  <section aria-labelledby="cart-items-heading" className="flex-1">
                    <h2 id="cart-items-heading" className="sr-only">
                      Items in your shopping cart
                    </h2>
                    <CartItems.Large
                      items={cartItems}
                      onIncrement={addToCart}
                      onDecrement={(product) => decrementProductQuantity(product.id)}
                      onDelete={removeFromCart}
                    />
                  </section>

                  <aside
                    aria-labelledby="summary-heading"
                    className="grid w-100 grid-cols-[1fr_min-content] justify-center gap-4 bg-[#f7f6f4] p-8"
                  >
                    <h3 className="col-span-2 text-center font-bold">Order Summary</h3>

                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Subtotal</dt>
                        <dd className="font-medium">{formatPrice(subtotal, CURRENCY)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Shipping</dt>
                        <dd className="font-medium">
                          {shipping === 0 ? "Free" : formatPrice(shipping, CURRENCY)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Tax (estimated)</dt>
                        <dd className="font-medium">{formatPrice(tax, CURRENCY)}</dd>
                      </div>
                      <div className="flex justify-between text-base font-bold">
                        <dt>Total</dt>
                        <dd>{formatPrice(total, CURRENCY)}</dd>
                      </div>
                    </dl>
                    <GlobalConsumer getters={["isLoggedIn"]} mutations={["checkout"]}>
                      {({ isLoggedIn, checkout, setIsAuthModalOpened }) => (
                        <Button
                          variant="default"
                          size="lg"
                          className="col-span-2"
                          disabled={checkout.status === "loading"}
                          onClick={() => {
                            if (isLoggedIn) {
                              checkout();
                            } else {
                              setIsAuthModalOpened(true);
                            }
                          }}
                        >
                          Proceed to Checkout
                        </Button>
                      )}
                    </GlobalConsumer>
                  </aside>
                </div>
              )}
            </div>
          );
        }}
      </GlobalConsumer>
      <div className="w-full bg-[rgb(247,247,247)]">
        <HomeSection
          className={cn(
            "grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-10",
            "mx-auto mb-0 p-4 md:p-8",
          )}
        >
          {homeData.services.map((service) => (
            <HomeServiceCard
              key={service.title}
              className="flex flex-col items-center justify-center gap-4"
            >
              <service.icon className="size-10 lg:size-16" />
              <p className="text-base lg:text-xl">{service.title}</p>
            </HomeServiceCard>
          ))}
        </HomeSection>
      </div>
    </>
  );
}

function calculateCartSubtotal(cartItems: Cart["items"]): number {
  return cartItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
}

function calculateShipping(_subtotal: number): number {
  // Free shipping over $100
  return 0; // $5.00 in cents
}

function calculateTax(_subtotal: number): number {
  // 8% tax rate
  return 0;
}

export function calculateCartTotals(cartItems: Cart["items"]) {
  const subtotal = calculateCartSubtotal(cartItems);
  const shipping = calculateShipping(subtotal);
  const tax = calculateTax(subtotal);
  const total = subtotal + shipping + tax;
  return { subtotal, shipping, tax, total };
}
